import { useState } from "react"
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { Delete as DeleteIcon } from "@mui/icons-material"
import { v4 as uuidv4 } from "uuid"
import { buildTwoFaImportFields } from "../clients/twoFaImportFields"
import { massOpsRunner } from "./massOpsRunnerBridge"
import type { MassOpItem } from "./massOpsRunner"
import { useMassOpsRunner } from "./useMassOpsRunner"
import { parseTdataZip } from "./tdataZipParser"
import { TdataDropzone } from "./tdataDropzone"

interface TdataRow {
  key: string
  zipName: string
  files: File[] | null
  parseError: string | null
}

type Phase = "importing" | "launching"

function phaseState(
  item: MassOpItem,
  phase: Phase,
): "pending" | "active" | "done" | "failed" {
  if (item.status === phase) return "active"
  if (item.status === "error" && item.failedPhase === phase) return "failed"
  if (phase === "importing") {
    // импорт прошёл, если выполнение дошло до запуска или завершилось
    if (item.status === "launching" || item.status === "done") return "done"
    return "pending"
  }
  return item.status === "done" ? "done" : "pending"
}

const PhaseCell = ({ item, phase }: { item: MassOpItem; phase: Phase }) => {
  const state = phaseState(item, phase)
  return (
    <TableCell>
      {state === "active" && <CircularProgress size={16} />}
      {state === "done" && <Chip size="small" color="success" label="✓" />}
      {state === "failed" && <Chip size="small" color="error" label="✗" />}
      {state === "pending" && "—"}
    </TableCell>
  )
}

export function TdataOnboardingTab() {
  const snapshot = useMassOpsRunner()
  const [rows, setRows] = useState<TdataRow[]>([])
  const [twoFaPassword, setTwoFaPassword] = useState("")
  const [twoFaPhoneDigits, setTwoFaPhoneDigits] = useState("")

  const batchItems = snapshot.items.filter((i) => i.batchKind === "tdata")
  const batchStarted = batchItems.length > 0

  const handleZips = async (zips: File[]) => {
    const parsed: TdataRow[] = []
    for (const zip of zips) {
      const res = await parseTdataZip(zip)
      parsed.push({
        key: uuidv4(),
        zipName: zip.name,
        files: res.files,
        parseError: res.error,
      })
    }
    setRows((prev) => [...prev, ...parsed])
  }

  const handleStart = () => {
    const twoFa = buildTwoFaImportFields(twoFaPassword, twoFaPhoneDigits)
    const ok = massOpsRunner.startTdataBatch(
      rows
        .filter((r) => r.files)
        .map((r) => ({
          label: r.zipName,
          files: r.files!,
          twoFa: Object.keys(twoFa).length > 0 ? twoFa : undefined,
        })),
    )
    if (ok) setRows([])
  }

  if (batchStarted) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">
            Обработано: {snapshot.completed + snapshot.failed} /{" "}
            {snapshot.total}
          </Typography>
          <Button
            size="small"
            onClick={() => massOpsRunner.clearFinished()}
            disabled={snapshot.hasActive}
          >
            Очистить завершённые
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Архив</TableCell>
              <TableCell>Импорт</TableCell>
              <TableCell>Запуск</TableCell>
              <TableCell>Ошибка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batchItems.map((item, idx) => (
              <TableRow key={item.key}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{item.label}</TableCell>
                <PhaseCell item={item} phase="importing" />
                <PhaseCell item={item} phase="launching" />
                <TableCell>
                  {item.error ? (
                    <Typography variant="caption" color="error">
                      {item.error}
                    </Typography>
                  ) : item.status === "done" ? (
                    <Chip size="small" color="success" label="Запущено" />
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Link href="/userbot/chains">Наблюдать в Цепочках</Link>
      </Stack>
    )
  }

  const validCount = rows.filter((r) => r.files).length

  return (
    <Stack spacing={2}>
      <TdataDropzone onFiles={handleZips} disabled={snapshot.hasActive} />

      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Архив</TableCell>
              <TableCell>Разбор</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={r.key}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{r.zipName}</TableCell>
                <TableCell>
                  {r.files ? (
                    `Готов (${r.files.length} файлов)`
                  ) : (
                    <Typography variant="caption" color="error">
                      {r.parseError}
                    </Typography>
                  )}
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton
                    size="small"
                    aria-label="Удалить строку"
                    onClick={() => setRows(rows.filter((x) => x.key !== r.key))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Stack
        spacing={1}
        sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}
      >
        <Typography variant="subtitle2">2FA (опционально)</Typography>
        <Typography variant="caption" color="text.secondary">
          Общий для всех аккаунтов: пароль ИЛИ число последних цифр телефона.
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="2FA-пароль"
            type="password"
            value={twoFaPassword}
            onChange={(e) => setTwoFaPassword(e.target.value)}
            fullWidth
            autoComplete="new-password"
          />
          <TextField
            size="small"
            label="Цифр телефона (1–15)"
            type="number"
            value={twoFaPhoneDigits}
            onChange={(e) => setTwoFaPhoneDigits(e.target.value)}
            inputProps={{ min: 1, max: 15 }}
            fullWidth
          />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          onClick={handleStart}
          disabled={validCount === 0 || snapshot.hasActive}
        >
          Импортировать и запустить ({validCount})
        </Button>
        <Typography variant="caption" color="text.secondary">
          Для каждого аккаунта: импорт tdata → запуск шаблона онбординга
        </Typography>
      </Stack>
    </Stack>
  )
}
